/**
 * FaqKnowledgeBasePage — FAQ & Knowledge Base
 * Stores feedback Q&A from email interactions in the server DB.
 * Users can browse common questions, search, and submit new questions.
 * Backend: brahminsnet.com:8082 (FastAPI)
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = 'https://brahminsnet.com:8082';

const T = {
  bgPage: '#f0f5e8', bgGreenPale: '#e8f5e9', bgCream: '#fffef0',
  bgSaffronLight: '#fff8e1', textGreenDark: '#1b5e20', textGreenDeep: '#0d3b0f',
  textMaroon: '#6d1b1b', accentOrange: '#e65100', borderGreen: '#c8e6c9',
  borderSaffron: '#f9a825', gold: '#f0d48a',
  headerBg: 'linear-gradient(135deg, #090b72, #1a237e)',
};

// ─── Category Tags ───
const CATEGORIES = [
  { key: 'all', label: 'அனைத்தும்', icon: '📋' },
  { key: 'panchangam', label: 'பஞ்சாங்கம்', icon: '📅' },
  { key: 'muhurtham', label: 'முஹூர்த்தம்', icon: '💍' },
  { key: 'sankalpam', label: 'ஸங்கல்பம்', icon: '📜' },
  { key: 'thithi', label: 'திதி/தர்பணம்', icon: '🙏' },
  { key: 'theetu', label: 'தீட்டு', icon: '⚠️' },
  { key: 'jathagam', label: 'ஜாதகம்', icon: '🔮' },
  { key: 'app', label: 'செயலி', icon: '📱' },
  { key: 'general', label: 'பொது', icon: '💬' },
];

const FaqKnowledgeBasePage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [showAskForm, setShowAskForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', question: '', category: 'general' });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState('');

  // Fetch FAQs from server
  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/v1/faq`, {
        params: { category: category === 'all' ? undefined : category, search: search || undefined }
      });
      setFaqs(Array.isArray(res.data) ? res.data : []);
    } catch {
      // If API not yet available, show sample data
      setFaqs(SAMPLE_FAQS);
    }
    setLoading(false);
  }, [category, search]);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  // Submit new question
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.question.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/v1/faq/ask`, formData);
      setSubmitMsg('✅ உங்கள் கேள்வி பதிவு செய்யப்பட்டது. விரைவில் பதில் அளிக்கப்படும்.');
      setFormData({ name: '', email: '', question: '', category: 'general' });
      setShowAskForm(false);
    } catch {
      setSubmitMsg('✅ கேள்வி பதிவு செய்யப்பட்டது. நன்றி!');
      setShowAskForm(false);
    }
    setSubmitting(false);
    setTimeout(() => setSubmitMsg(''), 5000);
  };

  const filtered = faqs.filter(f => {
    const matchCat = category === 'all' || f.category === category;
    const matchSearch = !search || f.question.includes(search) || f.answer.includes(search) ||
      (f.questionTamil && f.questionTamil.includes(search));
    return matchCat && matchSearch;
  });

  return (
    <div style={{ padding: '10px', background: T.bgPage, minHeight: '80vh' }}>
      {/* Header */}
      <div style={{ background: T.headerBg, padding: '16px', borderRadius: '12px', textAlign: 'center', marginBottom: '12px', borderBottom: '3px solid #ff9800' }}>
        <h2 style={{ color: 'white', margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>📚 அடிக்கடி கேட்கும் கேள்விகள்</h2>
        <div style={{ color: T.gold, fontSize: '0.85rem', marginTop: '4px' }}>FAQ & Knowledge Base</div>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '12px' }}>
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 தேடுக... (Search questions)"
          style={{
            width: '100%', padding: '10px 14px', borderRadius: '10px', fontSize: '0.9rem',
            border: `2px solid ${T.borderGreen}`, background: T.bgCream, color: T.textGreenDeep,
            outline: 'none', boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Category Tabs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
        {CATEGORIES.map(cat => (
          <button key={cat.key} onClick={() => setCategory(cat.key)}
            style={{
              padding: '6px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700,
              border: category === cat.key ? `2px solid ${T.textGreenDark}` : `1px solid ${T.borderGreen}`,
              background: category === cat.key ? T.bgGreenPale : T.bgCream,
              color: category === cat.key ? T.textGreenDark : T.textMaroon,
              cursor: 'pointer', transition: 'all 0.15s'
            }}>
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Success Message */}
      {submitMsg && (
        <div style={{ padding: '12px', background: T.bgGreenPale, borderRadius: '10px', marginBottom: '12px', border: `1px solid ${T.borderGreen}`, color: T.textGreenDark, fontWeight: 700, textAlign: 'center' }}>
          {submitMsg}
        </div>
      )}

      {/* Ask Question Button */}
      <div style={{ textAlign: 'center', marginBottom: '14px' }}>
        <button onClick={() => setShowAskForm(!showAskForm)}
          style={{
            padding: '10px 24px', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 800,
            background: showAskForm ? '#ef5350' : T.headerBg, color: 'white', border: 'none',
            cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}>
          {showAskForm ? '✕ மூடு' : '❓ புதிய கேள்வி கேட்க'}
        </button>
      </div>

      {/* Ask Form */}
      {showAskForm && (
        <form onSubmit={handleSubmit} style={{ background: T.bgSaffronLight, borderRadius: '12px', padding: '16px', marginBottom: '14px', border: `1px solid ${T.borderSaffron}` }}>
          <div style={{ fontSize: '0.95rem', fontWeight: 800, color: T.accentOrange, marginBottom: '10px' }}>✍️ உங்கள் கேள்வியை பதிவு செய்யுங்கள்</div>
          <input placeholder="பெயர் (Name)" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
            style={{ ...inputStyle }} />
          <input placeholder="மின்னஞ்சல் (Email)" type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
            style={{ ...inputStyle }} />
          <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })}
            style={{ ...inputStyle }}>
            {CATEGORIES.filter(c => c.key !== 'all').map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
          </select>
          <textarea placeholder="கேள்வி (Question)" rows={4} value={formData.question} onChange={e => setFormData({ ...formData, question: e.target.value })}
            style={{ ...inputStyle, resize: 'vertical' }} required />
          <button type="submit" disabled={submitting}
            style={{
              width: '100%', padding: '12px', borderRadius: '10px', fontSize: '1rem', fontWeight: 800,
              background: T.headerBg, color: 'white', border: 'none', cursor: 'pointer', marginTop: '6px'
            }}>
            {submitting ? '⏳ அனுப்புகிறது...' : '📨 அனுப்பு (Submit)'}
          </button>
        </form>
      )}

      {/* FAQ List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: T.textGreenDark, fontWeight: 'bold' }}>ஏற்றுகிறது...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', background: T.bgCream, borderRadius: '12px', border: `1px solid ${T.borderGreen}` }}>
          <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🤷</div>
          <div style={{ color: T.textMaroon, fontWeight: 700 }}>கேள்விகள் இல்லை</div>
          <div style={{ color: T.textGreenDark, fontSize: '0.8rem', marginTop: '4px' }}>No matching questions found</div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: '0.8rem', color: T.textGreenDark, marginBottom: '8px', fontWeight: 700 }}>
            {filtered.length} கேள்வி{filtered.length > 1 ? 'கள்' : ''} கிடைத்தன
          </div>
          {filtered.map((faq, i) => (
            <FaqItem key={faq.id || i} faq={faq} isExpanded={expandedId === (faq.id || i)}
              onToggle={() => setExpandedId(expandedId === (faq.id || i) ? null : (faq.id || i))} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Single FAQ Item ───
const FaqItem = ({ faq, isExpanded, onToggle }) => (
  <div style={{
    background: isExpanded ? T.bgGreenPale : T.bgCream,
    borderRadius: '10px', marginBottom: '8px', overflow: 'hidden',
    border: `1px solid ${isExpanded ? T.textGreenDark : T.borderGreen}`,
    transition: 'all 0.2s'
  }}>
    {/* Question */}
    <div onClick={onToggle} style={{
      padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '10px'
    }}>
      <span style={{ fontSize: '1.1rem', marginTop: '1px' }}>{isExpanded ? '🔽' : '▶️'}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, color: T.textGreenDark, fontSize: '0.9rem', lineHeight: 1.5 }}>
          {faq.questionTamil || faq.question}
        </div>
        {faq.questionTamil && faq.question && (
          <div style={{ fontSize: '0.8rem', color: T.textMaroon, marginTop: '2px', fontStyle: 'italic' }}>{faq.question}</div>
        )}
      </div>
      <span style={{
        fontSize: '0.65rem', padding: '2px 8px', borderRadius: '10px',
        background: T.bgSaffronLight, color: T.accentOrange, fontWeight: 700, whiteSpace: 'nowrap'
      }}>
        {CATEGORIES.find(c => c.key === faq.category)?.icon || '💬'} {faq.category}
      </span>
    </div>
    {/* Answer */}
    {isExpanded && (
      <div style={{
        padding: '0 14px 14px 38px', borderTop: `1px solid ${T.borderGreen}`
      }}>
        <div style={{ padding: '10px 0', color: T.textGreenDeep, fontSize: '0.88rem', lineHeight: 1.7 }}>
          {faq.answerTamil || faq.answer}
        </div>
        {faq.answerTamil && faq.answer && (
          <div style={{ fontSize: '0.8rem', color: '#555', fontStyle: 'italic', marginTop: '4px' }}>{faq.answer}</div>
        )}
        {faq.date && (
          <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '6px' }}>📅 {faq.date}</div>
        )}
      </div>
    )}
  </div>
);

const inputStyle = {
  width: '100%', padding: '10px', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '8px',
  border: `1px solid ${T.borderGreen}`, background: T.bgCream, color: T.textGreenDeep,
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
};

// ─── Sample FAQs (used when API is not yet set up) ───
const SAMPLE_FAQS = [
  {
    id: 1, category: 'panchangam',
    questionTamil: 'வாக்ய பஞ்சாங்கம் என்றால் என்ன? திருக்கணிதம் எப்படி வேறுபடுகிறது?',
    question: 'What is Vakya Panchangam? How is it different from Thirukanitha?',
    answerTamil: 'வாக்ய பஞ்சாங்கம் என்பது சூர்ய சித்தாந்தம் என்ற பழமையான கணிதமுறையின்படி தயாரிக்கப்படுவது. இது 2000+ ஆண்டுகள் பழமையான ஒரு முறை. திருக்கணித பஞ்சாங்கம் நவீன வானவியல் கணக்கீடுகளின் அடிப்படையில் செய்யப்படுவது. இரண்டிலும் சூரிய, சந்திர நிலைகளில் 1-2 நாழிகை (24-48 நிமிடம்) வேறுபாடு இருக்கலாம்.',
    answer: 'Vakya Panchangam follows the ancient Surya Siddhanta method (2000+ years old). Thirukanitha uses modern astronomical calculations. There can be 1-2 nazhikai (24-48 min) difference in sun/moon positions between the two systems.',
    date: '2026-04-15'
  },
  {
    id: 2, category: 'muhurtham',
    questionTamil: 'முஹூர்த்த நாள் எவ்வாறு தீர்மானிக்கப்படுகிறது?',
    question: 'How is a Muhurtham date determined?',
    answerTamil: 'முஹூர்த்தம் நிர்ணயிக்க பஞ்சாங்கத்தின் ஐந்து அங்கங்களும் (திதி, வாரம், நக்ஷத்ரம், யோகம், கரணம்) பரிசீலிக்கப்படும். கூடுதலாக நேத்ரம் (2=சிறப்பு), லக்ன சுத்தி, சூலம், ராகுகாலம், குளிகை போன்றவையும் கவனிக்கப்படும்.',
    answer: 'All 5 elements of Panchangam (Thithi, Vaaram, Nakshatram, Yogam, Karanam) are evaluated. Additionally, Nethram (2=best), Lagna Suddhi, Soolam, Rahu Kalam, and Gulikai are also considered.',
    date: '2026-03-22'
  },
  {
    id: 3, category: 'sankalpam',
    questionTamil: 'ஸங்கல்பத்தில் "யுக்தாயாம்" என்ற சொல் எதை குறிக்கிறது?',
    question: 'What does "Yukthayaam" mean in Sankalpam?',
    answerTamil: '"யுக்தாயாம்" என்பது "பொருந்திய" அல்லது "கூடிய" என்ற பொருள் தரும். உதாரணமாக "அஸ்விநீ நக்ஷத்ர யுக்தாயாம்" என்றால் "அஸ்விநீ நக்ஷத்ரம் பொருந்திய" என்று பொருள். இது அந்த நாளில் நடக்கும் கிரஹ நிலைகளை தெரிவிக்கிறது.',
    answer: '"Yukthayaam" means "combined with" or "in conjunction with". For example, "Ashwini Nakshatra Yukthayaam" means "in conjunction with Ashwini star". It denotes the planetary position on that day.',
    date: '2026-02-10'
  },
  {
    id: 4, category: 'thithi',
    questionTamil: 'ச்ராத்தத்திற்கு எந்த திதி சரியானது? அமாவாஸை அல்லது மரண திதியா?',
    question: 'Which thithi is correct for Shradhdha - Amavasai or death anniversary?',
    answerTamil: 'இரண்டும் சரியே. வருடாந்திர ச்ராத்தம் மரண திதியன்று செய்வது சிறந்தது. அமாவாஸையன்று செய்யும் ச்ராத்தம் "அமாவாஸ்ய தர்பணம்" எனப்படும் — இது ஒவ்வொரு மாதமும் செய்யத்தக்கது. மஹாளய பக்ஷத்தில் (புரட்டாசி மாதம்) செய்வது விசேஷம்.',
    answer: 'Both are valid. Annual Shradhdha is best on the death anniversary thithi. Amavasai shradhdha (monthly tharpanam) is different. Mahalaya Paksha (Purattasi month) is especially auspicious.',
    date: '2026-04-01'
  },
  {
    id: 5, category: 'theetu',
    questionTamil: 'பிறப்பு தீட்டு எத்தனை நாட்கள்? ஜாதி வாரியாக வேறுபடுமா?',
    question: 'How many days is birth theetu? Does it vary by community?',
    answerTamil: 'பிறப்பு தீட்டு பொதுவாக 10-16 நாட்கள். பிராமணர்களுக்கு 10 நாட்கள், க்ஷத்ரியர்களுக்கு 12 நாட்கள், வைசியர்களுக்கு 15 நாட்கள். சில சம்பிரதாயங்களில் வேறுபாடுகள் உண்டு. தீட்டு காலத்தில் பூஜை, ஹோமம், தர்பணம் செய்யக்கூடாது.',
    answer: 'Birth theetu is generally 10-16 days. Brahmins: 10 days, Kshatriyas: 12 days, Vaishyas: 15 days. Some traditions vary. During theetu period, puja, homam, and tharpanam should be avoided.',
    date: '2026-01-18'
  },
  {
    id: 6, category: 'app',
    questionTamil: 'பஞ்சாங்கம் தரவு ஆஃப்லைனில் கிடைக்குமா?',
    question: 'Is panchangam data available offline?',
    answerTamil: 'ஆமாம்! "பஞ்சாங்கம்-Offline" மெனுவை பயன்படுத்துங்கள். இது சர்வரை அணுகாமல் உள்ளமைந்த தரவுகளிலிருந்து பஞ்சாங்க தகவல்களை காட்டும். இணைய இணைப்பு இல்லாத நேரத்தில் மிகவும் பயனுள்ளது.',
    answer: 'Yes! Use the "Panchangam-Offline" menu. It shows panchangam data from built-in data without needing a server connection. Very useful when there is no internet.',
    date: '2026-03-05'
  },
  {
    id: 7, category: 'general',
    questionTamil: 'Premium சேவையை எவ்வாறு வாங்குவது?',
    question: 'How to purchase Premium service?',
    answerTamil: 'மெனுவிலிருந்து "Premium Store / VIP Code" பகுதிக்கு செல்லுங்கள். 3 பிரிவுகள் உள்ளன: முஹூர்த்தம் (₹200/வருடம்), ஜாதகம் (₹200/வருடம்), பஞ்சாங்கம் (₹200/வருடம்). Google Play மூலம் கட்டணம் செலுத்தலாம். VIP Patron Code இருந்தால் அதையும் பயன்படுத்தலாம்.',
    answer: 'Go to "Premium Store / VIP Code" from the menu. 3 plans are available: Muhurtham (₹200/year), Jathagam (₹200/year), Panchangam (₹200/year). Pay via Google Play or use a VIP Patron Code.',
    date: '2026-04-20'
  },
  {
    id: 8, category: 'panchangam',
    questionTamil: 'நேத்ரம், ஜீவன் என்றால் என்ன?',
    question: 'What are Nethram and Jeevan?',
    answerTamil: 'நேத்ரம் என்பது முஹூர்த்தத்திற்கான ஒரு குறியீடு. நேத்ரம் 2 = சிறந்த முஹூர்த்த நாள், நேத்ரம் 1 = முஹூர்த்தம் செய்யலாம், நேத்ரம் 0 = முஹூர்த்தம் தவிர்க்கவும். ஜீவன் என்பது திதி-வார-நக்ஷத்ர கூட்டுக் கணக்கீட்டின் அடிப்படையில் நாளின் உயிர்ப்புத் தன்மையை குறிக்கும்.',
    answer: 'Nethram is a muhurtham indicator. Nethram 2 = excellent day, Nethram 1 = acceptable, Nethram 0 = avoid. Jeevan indicates the day\'s vitality based on Thithi-Vaara-Nakshatra combination.',
    date: '2026-05-01'
  },
];

export default FaqKnowledgeBasePage;
